import React, { useEffect, useState, useRef } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Helper function to parse markdown-style content
const parseStoryContent = (description = "") => {
  const lines = description.split("\n").map(l => l.trim()).filter(Boolean);
  const paragraphs = [];
  const bullets = [];
  let currentPara = "";
  
  for (const line of lines) {
    if (line.startsWith("## ") || line.startsWith("### ")) continue;
    
    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (currentPara) {
        paragraphs.push(currentPara);
        currentPara = "";
      }
      bullets.push(line.replace(/^[-*]\s+/, "").replace(/\*\*/g, ""));
    } 
    else {
      currentPara += (currentPara ? " " : "") + line.replace(/\*\*/g, "");
    }
  }
  
  if (currentPara) paragraphs.push(currentPara);
  
  return { paragraphs, bullets };
};

// Star Rating Component
const StarRating = ({ count = 4 }) => (
  <ul className="rating" style={{ listStyle: "none", padding: 0, display: "flex", gap: 2, justifyContent: "center" }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <li key={s}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14" height="14" viewBox="0 0 24 24"
          fill={s <= count ? "currentColor" : "none"}
          stroke="currentColor" strokeWidth="2"
          style={{ color: s <= count ? "#f4a537" : "#ccc" }}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </li>
    ))}
  </ul>
);

// Custom Slider Component - Updated for mobile view (shows 1 card on mobile)
const AutoSlider = ({ children, slidesToShow = 3, autoplaySpeed = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const sliderRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setSlidesPerView(1); // Mobile: 1 card
      } else if (width < 992) {
        setSlidesPerView(2); // Tablet: 2 cards
      } else if (width < 1200) {
        setSlidesPerView(3); // Small desktop: 3 cards
      } else {
        setSlidesPerView(slidesToShow); // Large desktop: configured number
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [slidesToShow]);

  const totalSlides = React.Children.count(children);
  const maxIndex = Math.max(0, totalSlides - slidesPerView);

  useEffect(() => {
    if (totalSlides <= slidesPerView) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // If we're at the last possible index, go back to the beginning
        if (prevIndex >= maxIndex) {
          return 0;
        }
        // Otherwise, move to the next slide
        return prevIndex + 1;
      });
    }, autoplaySpeed);

    return () => clearInterval(intervalRef.current);
  }, [totalSlides, slidesPerView, maxIndex, autoplaySpeed]);

  const handlePrev = () => {
    clearInterval(intervalRef.current);
    setCurrentIndex((prevIndex) => {
      if (prevIndex <= 0) {
        return maxIndex; // Go to the last possible index
      }
      return prevIndex - 1;
    });
  };

  const handleNext = () => {
    clearInterval(intervalRef.current);
    setCurrentIndex((prevIndex) => {
      if (prevIndex >= maxIndex) {
        return 0; // Go back to the beginning
      }
      return prevIndex + 1;
    });
  };

  if (totalSlides <= slidesPerView) {
    return (
      <div className="slider-container" ref={sliderRef}>
        <div className="slider-track" style={{ 
          display: 'flex', 
          gap: '24px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {children}
        </div>
      </div>
    );
  }

  // Calculate transform percentage based on currentIndex
  const transformValue = `translateX(-${currentIndex * (100 / slidesPerView)}%)`;

  return (
    <div className="slider-container" ref={sliderRef}>
      <div className="slider-track" style={{ 
        display: 'flex', 
        gap: '24px', 
        transition: 'transform 0.5s ease-in-out',
        transform: transformValue
      }}>
        {React.Children.map(children, (child) => (
          <div style={{ 
            flex: `0 0 ${100 / slidesPerView}%`,
            minWidth: 0
          }}>
            {child}
          </div>
        ))}
      </div>
      
      {/* Navigation Buttons */}
      {totalSlides > slidesPerView && (
        <>
          <button 
            onClick={handlePrev}
            className="slider-nav prev"
            aria-label="Previous slide"
            style={{
              position: 'absolute',
              left: window.innerWidth < 768 ? '5px' : '-15px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#000000',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: window.innerWidth < 768 ? '35px' : '40px',
              height: window.innerWidth < 768 ? '35px' : '40px',
              cursor: 'pointer',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            <i className="fa-solid fa-chevron-left" style={{ fontSize: window.innerWidth < 768 ? '12px' : '14px' }}></i>
          </button>
          
          <button 
            onClick={handleNext}
            className="slider-nav next"
            aria-label="Next slide"
            style={{
              position: 'absolute',
              right: window.innerWidth < 768 ? '5px' : '-15px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#000000',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: window.innerWidth < 768 ? '35px' : '40px',
              height: window.innerWidth < 768 ? '35px' : '40px',
              cursor: 'pointer',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            <i className="fa-solid fa-chevron-right" style={{ fontSize: window.innerWidth < 768 ? '12px' : '14px' }}></i>
          </button>
        </>
      )}

      {/* Dots Indicator */}
      <div className="slider-dots" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: window.innerWidth < 768 ? '6px' : '8px',
        marginTop: window.innerWidth < 768 ? '20px' : '30px',
        flexWrap: 'wrap'
      }}>
        {Array.from({ length: maxIndex + 1 }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            style={{
              width: index === currentIndex ? (window.innerWidth < 768 ? '16px' : '20px') : (window.innerWidth < 768 ? '6px' : '10px'),
              height: window.innerWidth < 768 ? '6px' : '10px',
              borderRadius: index === currentIndex ? (window.innerWidth < 768 ? '6px' : '10px') : '50%',
              border: 'none',
              background: index === currentIndex ? '#000000' : '#cccccc',
              cursor: 'pointer',
              padding: 0,
              transition: 'all 0.3s ease'
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Aboutus = () => {
  // Only fetch the first section (story) from API
  const [storyData, setStoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for reviews
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState(null);

  // Fetch only the first item from about API
  useEffect(() => {
    fetch("https://evercorenutrition.com/ev_admin/api/about/")
      .then(response => {
        if (!response.ok) throw new Error("Failed to fetch");
        return response.json();
      })
      .then(data => {
        // Get the first item from the array (as per your requirement)
        if (data && data.length > 0) {
          setStoryData(data[0]); // Set the first item as story data
        } else {
          setStoryData(null);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching about data:", err);
        setError(err.message);
        setLoading(false);
      });
  }, []);
  
  // Fetch reviews from API
  useEffect(() => {
    fetch("https://evercorenutrition.com/ev_admin/api/reviews/approved/")
      .then(response => {
        if (!response.ok) throw new Error("Failed to fetch reviews");
        return response.json();
      })
      .then(data => {
        // Handle the API response format with count, next, previous, and results
        // The 'results' object contains a 'reviews' array
        if (data.results && data.results.reviews && Array.isArray(data.results.reviews)) {
          // Filter only approved reviews for public display (though endpoint might already return approved)
          const approvedReviews = data.results.reviews.filter(review => review.status === 'approved');
          
          // Shuffle and get 6 random reviews (or all if less than 6)
          const shuffled = [...approvedReviews].sort(() => 0.5 - Math.random());
          const randomReviews = shuffled.slice(0, 6);
          
          setReviews(randomReviews);
        } else {
          console.warn('Unexpected API response structure:', data);
          setReviews([]);
        }
        setReviewsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching reviews:", err);
        setReviewsError(err.message);
        setReviewsLoading(false);
      });
  }, []);

  const { paragraphs, bullets } = storyData 
    ? parseStoryContent(storyData.description) 
    : { paragraphs: [], bullets: [] };

  // Team members data (static)
  const teamMembers = [
    { name: "Rahul Patel", role: "Founder & CEO", desc: "Leading Evercore with passion, ensuring premium protein products for every fitness enthusiast." },
    { name: "Mahek Shah", role: "Product Research Head", desc: "Develops science-based formulas to deliver pure, effective, and result-driven nutrition supplements." },
    { name: "Mahesh Patel", role: "Creative Lead", desc: "Creates bold branding, strong visuals, and inspiring campaigns for Evercore's fitness vision." },
    { name: "Laksh Agraval", role: "Quality Manager", desc: "Maintains strict quality checks, ensuring every product meets trusted safety and purity standards." },
    { name: "Kishan Varma", role: "Customer Success Manager", desc: "Guides customers, offering support and helping them choose perfect supplements for their goals." }
  ];

  return (
    <>
      <Navbar />
      
      {/* Breadcrumb Section */}
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>About Us</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <a href="/"><i className="fa-solid fa-house"></i></a>
                    </li>
                    <li className="breadcrumb-item active">About Us</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section - Fetched from API */}
      <section className="fresh-vegetable-section section-lg-space">
        <div className="container-fluid-lg">
          <div className="row gx-xl-5 gy-xl-0 g-3 ratio_148_1">
            <div className="col-xl-6 col-12">
              <div className="row g-sm-4 g-2">
                <div className="col-12">
                  <div className="fresh-image-2">
                    <div>
                      <img
                        src={storyData?.image || "/assets/images/placeholder.jpg"}
                        alt={storyData?.title || "Our Story"}
                        className="resimg"
                        width="80%"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-6 col-12">
              <div className="fresh-contain p-center-left">
                <div>
                  <div className="review-title">
                    <h4>About Us</h4>
                    <h2>{loading ? "Your Trusted Online Shopping Destination" : storyData?.title || "Your Trusted Nutrition Destination"}</h2>
                  </div>

                  <div className="delivery-list">
                    {loading ? (
                      <p className="text-content">Loading story...</p>
                    ) : error ? (
                      <>
                        <p className="text-content" style={{ textAlign: "justify" }}>
                          Founded in 2020, our journey began with a simple vision — to make quality products
                          accessible and affordable for everyone. What started as a small online store has now
                          grown into a trusted eCommerce brand serving thousands of happy customers across the country.
                        </p>
                        <ul className="delivery-box">
                          {[
                            { icon: "delivery.svg", text: "Free delivery for all orders" },
                            { icon: "leaf.svg", text: "Top Quality Products" },
                            { icon: "delivery.svg", text: "Genuine & Authentic Items" },
                            { icon: "leaf.svg", text: "Trusted & Verified" }
                          ].map((item, index) => (
                            <li key={index}>
                              <div className="delivery-box">
                                <div className="delivery-icon">
                                  <img src={`../assets/svg/3/${item.icon}`} className="blur-up lazyload" alt="img" />
                                </div>
                                <div className="delivery-detail">
                                  <h5 className="text">{item.text}</h5>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        {paragraphs.map((para, index) => (
                          <p key={index} className="text-content" style={{ textAlign: "justify", marginBottom: "12px" }}>
                            {para}
                          </p>
                        ))}
                        
                        {bullets.length > 0 && (
                          <ul className="delivery-box">
                            {bullets.map((bullet, index) => (
                              <li key={index}>
                                <div className="delivery-box">
                                  <div className="delivery-icon">
                                    <img
                                      src={index % 2 === 0 ? "../assets/svg/3/delivery.svg" : "../assets/svg/3/leaf.svg"}
                                      className="blur-up lazyload"
                                      alt="icon"
                                    />
                                  </div>
                                  <div className="delivery-detail">
                                    <h5 className="text">{bullet}</h5>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Quality Section - Static */}
      <section className="client-section section-lg-space">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="about-us-title text-center">
                <h4>What We Do</h4>
                <h2 className="center">We are Trusted by Clients</h2>
              </div>

              <div className="row g-4">
                {[
                  { icon: "work.svg", stat: "10", label: "Business Years", text: "A decade of delivering premium supplements trusted by thousands of customers worldwide." },
                  { icon: "buy.svg", stat: "80 K+", label: "Products Sales", text: "Over 80,000 products sold to happy customers across India with 99% satisfaction rate." },
                  { icon: "user.svg", stat: "90%", label: "Happy Customers", text: "90% of our customers recommend Evercore to friends and family for their fitness journey." }
                ].map((card, index) => (
                  <div key={index} className="col-lg-4 col-md-6">
                    <div className="clint-contain h-100">
                      <div className="client-icon">
                        <img
                          src={`../assets/svg/3/${card.icon}`}
                          alt="img"
                          style={{
                            filter: "brightness(0) saturate(100%) invert(39%) sepia(54%) saturate(700%) hue-rotate(346deg) brightness(95%) contrast(90%)",
                          }}
                        />
                      </div>
                      <h2>{card.stat}</h2>
                      <h4>{card.label}</h4>
                      <p className="text-content">{card.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section with Auto Slider - Static */}
      <section className="team-section section-lg-space">
        <div className="container-fluid-lg">
          <div className="about-us-title text-center">
            <h4 className="text-content">Our Creative Team</h4>
            <h2 className="center">Evercore team member</h2>
          </div>
          <div className="row">
            <div className="col-12 position-relative">
              <AutoSlider slidesToShow={4} autoplaySpeed={4000}>
                {teamMembers.map((member, index) => (
                  <div key={index} className="team-card-wrapper">
                    <div className="team-box h-100">
                      <div className="team-image">
                        <img
                          src="/assets/images/user.png"
                          className="img-fluid blur-up lazyload"
                          alt={member.name}
                        />
                      </div>
                      <div className="team-name">
                        <h3>{member.name}</h3>
                        <h5>{member.role}</h5>
                        <p className="team-description">{member.desc}</p>
                        <ul className="team-media">
                          <li>
                            <a href="https://www.facebook.com/" className="fb-bg">
                              <i className="fa-brands fa-facebook-f"></i>
                            </a>
                          </li>
                          <li>
                            <a href="https://twitter.com/" className="twitter-bg">
                              <i className="fa-brands fa-twitter"></i>
                            </a>
                          </li>
                          <li>
                            <a href="https://www.instagram.com/" className="insta-bg">
                              <i className="fa-brands fa-instagram"></i>
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </AutoSlider>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Now using API data */}
      {/* ── Testimonials ── */}
<section className="review-section section-lg-space">
  <div className="container-fluid">
    <div className="about-us-title text-center">
      <h4 className="text-content">Latest Testimonials</h4>
      <h2 className="center">What people say</h2>
    </div>
    <div className="row">
      <div className="col-12">
        {/* Pure flex — no slider */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center", marginTop: 24 }}>
          {reviewsLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading testimonials...</span>
              </div>
            </div>
          ) : reviewsError ? (
            <div className="text-center py-4">
              <p className="text-danger">Unable to load testimonials at this time.</p>
            </div>
          ) : reviews.length > 0 ? (
            reviews.map((review) => (
              <div key={review.id} className="reviewer-box" style={{ flex: "1 1 260px", maxWidth: 340 }}>
                <i className="fa-solid fa-quote-right"></i>
                <div className="product-rating">
                  <StarRating count={review.rating || 5} />
                </div>
                <h3>{review.customer_name}</h3>
                <p>"{review.review}"</p>
                <div className="reviewer-profile">
                  <div className="reviewer-image">
                    <img 
                      src="/assets/images/user.png" 
                      className="blur-up lazyload" 
                      alt={review.customer_name}
                      onError={(e) => { e.target.src = "/assets/images/user.png"; }}
                    />
                  </div>
                  <div className="reviewer-name">
                    <h4>{review.customer_name}</h4>
                    <h6>Verified Customer</h6>
                    {review.created_at && (
                      <small className="text-muted d-block">
                        {new Date(review.created_at).toLocaleDateString()}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">
              <p>No verified customer reviews yet. Be the first to share your experience!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
</section>

      <style>{`
        .team-section,
        .review-section {
          position: relative;
          overflow: hidden;
          padding: 60px 0;
        }
        
        .slider-container {
          position: relative;
          overflow: hidden;
          padding: 20px 40px;
        }
        
        .slider-track {
          display: flex;
          gap: 24px;
          transition: transform 0.5s ease-in-out;
        }
        
        .team-card-wrapper,
        .testimonial-card-wrapper {
          height: 100%;
          min-width: 0;
        }
        
        .team-box,
        .reviewer-box {
          background: #fff;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 380px;
        }
        
        .team-box:hover,
        .reviewer-box:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 20px rgba(0,0,0,0.15);
        }
        
        .team-image {
          text-align: center;
          margin-bottom: 15px;
          flex-shrink: 0;
        }
        
        .team-image img {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #f4a537;
        }
        
        .team-name {
          text-align: center;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .team-name h3 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 5px;
          color: #333;
        }
        
        .team-name h5 {
          font-size: 14px;
          color: #f4a537;
          margin-bottom: 10px;
          font-weight: 500;
        }
        
        .team-description {
          font-size: 13px;
          color: #666;
          line-height: 1.5;
          margin-bottom: 15px;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .team-media {
          display: flex;
          justify-content: center;
          gap: 10px;
          padding: 0;
          margin: 0;
          list-style: none;
          flex-shrink: 0;
        }
        
        .team-media li a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          color: #fff;
          transition: all 0.3s ease;
        }
        
        .team-media li a:hover {
          transform: translateY(-3px);
        }
        
        .fb-bg { background: #3b5998; }
        .twitter-bg { background: #1da1f2; }
        .insta-bg { background: #e4405f; }
        
        .reviewer-box {
          text-align: center;
          position: relative;
        }
        
        .reviewer-box i.fa-quote-right {
          font-size: 30px;
          color: rgba(244, 165, 55, 0.2);
          position: absolute;
          top: 15px;
          right: 15px;
        }
        
        .product-rating {
          margin-bottom: 15px;
        }
        
        .testimonial-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 10px;
          color: #333;
        }
        
        .testimonial-text {
          font-size: 13px;
          color: #666;
          line-height: 1.6;
          margin-bottom: 20px;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .reviewer-profile {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 15px;
          border-top: 1px solid #eee;
          margin-top: auto;
          flex-shrink: 0;
        }
        
        .reviewer-image {
          flex-shrink: 0;
        }
        
        .reviewer-image img {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #f4a537;
        }
        
        .reviewer-name {
          text-align: left;
          flex: 1;
        }
        
        .reviewer-name h4 {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 2px;
          color: #333;
        }
        
        .reviewer-name h6 {
          font-size: 12px;
          color: #f4a537;
          margin-bottom: 2px;
          font-weight: 500;
        }
        
        .reviewer-name small {
          font-size: 11px;
          color: #999;
        }
        
        .slider-nav {
          opacity: 0.7;
          transition: all 0.3s ease;
        }
        
        .slider-nav:hover {
          opacity: 1;
          background: #333333 !important;
        }
        
        /* Mobile Styles - 1 card on mobile */
        @media (max-width: 767px) {
          .team-section,
          .review-section {
            padding: 40px 0;
          }
          
          .slider-container {
            padding: 10px 15px !important;
            margin: 0 !important;
          }
          
          .slider-track {
            gap: 15px !important;
          }
          
          .team-box,
          .reviewer-box {
            min-height: 340px;
            padding: 15px 12px;
            margin: 0 5px;
          }
          
          .team-image img {
            width: 90px;
            height: 90px;
          }
          
          .team-name h3 {
            font-size: 16px;
          }
          
          .team-name h5 {
            font-size: 13px;
            margin-bottom: 8px;
          }
          
          .team-description {
            font-size: 12px;
            line-height: 1.4;
            margin-bottom: 12px;
            -webkit-line-clamp: 3;
          }
          
          .team-media li a {
            width: 28px;
            height: 28px;
          }
          
          .team-media li a i {
            font-size: 12px;
          }
          
          /* Testimonials */
          .reviewer-box i.fa-quote-right {
            font-size: 24px;
            top: 10px;
            right: 10px;
          }
          
          .testimonial-title {
            font-size: 15px;
            margin-bottom: 8px;
            padding: 0 5px;
          }
          
          .testimonial-text {
            font-size: 12px;
            line-height: 1.5;
            margin-bottom: 15px;
            -webkit-line-clamp: 3;
            padding: 0 5px;
          }
          
          .reviewer-profile {
            padding-top: 12px;
            gap: 8px;
          }
          
          .reviewer-image img {
            width: 45px;
            height: 45px;
          }
          
          .reviewer-name h4 {
            font-size: 13px;
          }
          
          .reviewer-name h6 {
            font-size: 11px;
          }
          
          .reviewer-name small {
            font-size: 10px;
          }
          
          /* Dots */
          .slider-dots {
            margin-top: 20px !important;
          }
        }
        
        /* Extra small devices */
        @media (max-width: 380px) {
          .team-box,
          .reviewer-box {
            min-height: 320px;
            padding: 12px 10px;
          }
          
          .team-image img {
            width: 80px;
            height: 80px;
          }
          
          .team-description {
            -webkit-line-clamp: 2;
          }
          
          .testimonial-text {
            -webkit-line-clamp: 2;
          }
          
          .reviewer-image img {
            width: 40px;
            height: 40px;
          }
        }
        
        /* Fix for clint-contain */
        .clint-contain {
          background: #fff;
          padding: 30px 20px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .clint-contain:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 20px rgba(0,0,0,0.15);
        }
        
        .clint-contain .client-icon {
          margin-bottom: 20px;
        }
        
        .clint-contain h2 {
          font-size: 36px;
          font-weight: 700;
          color: #f4a537;
          margin-bottom: 10px;
        }
        
        .clint-contain h4 {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin-bottom: 15px;
        }
        
        .clint-contain p {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
          margin-bottom: 0;
        }
      `}</style>

      <Footer />
    </>
  );
};

export default Aboutus;